import React from 'react';
import i18n from "../i18n/i18n";

// --- Icon SVGs (Inline definitions for single-file component) ---

// Thin, elegant cross for the Cancel button
const IconX: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18M6 6l12 12"/>
  </svg>
);

// Thin, elegant checkmark for the Confirm button
const IconCheck: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5"/>
  </svg>
);

// Headphones icon for the main graphic
const IconHeadphones: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
    <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
  </svg>
);

// --- Component Definition ---

interface AudioSettingsModalProps {
  /** Callback function when user makes a decision: true for 'Yes', false for 'No' */
  onDecision: (startMusic: boolean) => void;
  /** Controls the visibility of the modal */
  isOpen: boolean;
}

const AudioSettingsModal: React.FC<AudioSettingsModalProps> = ({ onDecision, isOpen }) => {
  if (!isOpen) return null;

  // Custom class names for the unique, complex glow effects and the new glass effect
  const headphoneIconClass = "headphone-icon-white"; // White headphones
  const iconGlowClass = "icon-glow-text-white"; // White icon with pink glow
  const cardGlassClass = "modal-card-glass-light"; 
  const decisionButtonStyle = "decision-button-style-square"; // Square buttons

  return (
    <>
      {/* Custom Styles for glow/shadow effects, and the light glass blur */}
      <style>
        {`
        /* Card Background: Light Glass Effect (Frosted) */
        .${cardGlassClass} {
          background-color: rgba(255, 255, 255, 0.2); 
          backdrop-filter: blur(8px); 
          -webkit-backdrop-filter: blur(8px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        /* Headphone Icon: White color */
        .${headphoneIconClass} {
          color: white; /* Set the icon color to white */
          /* Subtle shadow for a clean look */
          filter: drop-shadow(0 0 4px rgba(255, 255, 255, 0.4)); 
        }
        
        /* Inner Icon Glow (X and Check) - White icon with Pink glow */
        .${iconGlowClass} {
          color: white; /* Icon stroke color is white */
          /* Pinkish-orange glow effect */
          text-shadow: 0 0 8px #ff8966, 0 0 15px #ff6b4d; 
        }

        /* Square Button Design */
        .${decisionButtonStyle} {
          /* Dark, opaque background */
          background-color: rgba(0, 0, 0, 0.4); 
          /* Smooth transition */
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          border-radius: 12px; /* Rounded corners */
        }
        
        /* Hover Effect for square buttons */
        .${decisionButtonStyle}:hover {
          transform: scale(1.05);
          /* Subtle outer shadow on hover */
          box-shadow: 0 0 15px rgba(255, 255, 255, 0.2); 
        }
        `}
      </style>

      {/* Modal Backdrop: Fixed position, dark overlay, centered content */}
      <div 
        className="fixed inset-0 bg-black/70 flex justify-center items-center z-50 p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
             // You might want to call onDecision(false) here to close when clicking the backdrop
          }
        }}
      >
        {/* The Modal Card: Light Glass effect applied here */}
        <div className={`p-8 rounded-2xl text-center shadow-2xl max-w-sm w-full md:max-w-md ${cardGlassClass}`}>
          
          {/* Headphone Icon (White) */}
          <div className={`mx-auto mb-6 h-16 w-16 ${headphoneIconClass}`}>
            <IconHeadphones strokeWidth={1.5} className="h-full w-full" />
          </div>
          
          {/* Main Question (Music Content) */}
          <h2 className="text-2xl font-semibold text-white mb-1">
            {i18n.t("start_music")}
          </h2>
          
          {/* Secondary Text (Music Content) */}
          <p className="text-base text-gray-200 mb-8 italic font-light">
              {i18n.t("start_music_message")}
          </p>
          
          {/* Action Buttons Group */}
          <div className="flex justify-around items-center">
            
            {/* 'No' Button (X) - Square design, white icon with pink glow */}
            <button 
              className={`h-20 w-20 border-none ${decisionButtonStyle} text-[#ffb8a3] p-0 flex items-center justify-center`}
              onClick={() => onDecision(false)}
              aria-label="No, close this prompt"
            >
              <div className={iconGlowClass}>
                <IconX strokeWidth={3} className="h-10 w-10" />
              </div>
            </button>
            
            {/* 'Yes' Button (Checkmark) - Square design, white icon with pink glow */}
            <button 
              className={`h-20 w-20 border-none ${decisionButtonStyle} text-[#ffb8a3] p-0 flex items-center justify-center`}
              onClick={() => onDecision(true)}
              aria-label="Yes, open audio settings"
            >
              <div className={iconGlowClass}>
                <IconCheck strokeWidth={3} className="h-10 w-10" />
              </div>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AudioSettingsModal;